/** KPI card (MASTER §6): uppercase label → big number → optional delta + sparkline. */
import { TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';

import { cn } from './cn.js';
import { Skeleton } from './Feedback.js';

export interface KpiCardProps {
  label: ReactNode;
  value: ReactNode;
  icon?: LucideIcon;
  /** Tone of the leading icon badge. */
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  delta?: { value: string; positive: boolean } | null;
  hint?: ReactNode;
  spark?: number[];
  loading?: boolean;
  className?: string;
}

const TONE: Record<NonNullable<KpiCardProps['tone']>, string> = {
  primary: 'bg-primary/15 text-primary',
  success: 'bg-success/15 text-success',
  warning: 'bg-warning/15 text-warning',
  danger: 'bg-danger/15 text-danger',
  info: 'bg-info/15 text-info',
};

export function KpiCard({
  label,
  value,
  icon: Icon,
  tone = 'primary',
  delta,
  hint,
  spark,
  loading,
  className,
}: KpiCardProps): ReactNode {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border border-border bg-surface p-5',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</p>
          {loading ? (
            <Skeleton className="mt-2 h-8 w-28" />
          ) : (
            <p className="mt-1.5 font-display text-3xl font-bold leading-none text-fg">{value}</p>
          )}
          {hint && !loading ? <p className="mt-1.5 text-xs text-faint">{hint}</p> : null}
        </div>
        {Icon ? (
          <span className={cn('grid h-10 w-10 shrink-0 place-items-center rounded-lg', TONE[tone])}>
            <Icon className="h-5 w-5" aria-hidden />
          </span>
        ) : null}
      </div>

      {delta && !loading ? (
        <div
          className={cn(
            'mt-3 inline-flex items-center gap-1 text-xs font-semibold',
            delta.positive ? 'text-success' : 'text-danger',
          )}
        >
          {delta.positive ? (
            <TrendingUp className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <TrendingDown className="h-3.5 w-3.5" aria-hidden />
          )}
          {delta.value}
        </div>
      ) : null}

      {spark && spark.length > 1 && !loading ? (
        <div className="mt-3 h-10">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={spark.map((v, i) => ({ i, v }))}
              margin={{ top: 2, bottom: 0, left: 0, right: 0 }}
            >
              <defs>
                <linearGradient id={`spark-${tone}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgb(var(--primary))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="rgb(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke="rgb(var(--primary))"
                strokeWidth={2}
                fill={`url(#spark-${tone})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </div>
  );
}
